/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useContext, useEffect, useRef, useState } from 'react'
import {formatWeight, formatWeightShort} from '../../../helpers/WeightHelpers'
import ApexCharts, { ApexOptions } from 'apexcharts'
import { getCSS, getCSSVariableValue } from '../../../assets/ts/_utils'
import { useThemeMode } from '../../layout/theme-mode/ThemeModeProvider'
import { BLACKBOXES_BLACKBOX_GRAPHIC_GET_MONTLY_WEIGHT_URL,BLACKBOXES_GET_INDICATOR_GROUP_BY_DISTRIBUTORID,
   BLACKBOXES_GET_INDICATOR_GROUP_BY_COMPANYID } from '../../../../urls'
import { blackboxIdContext } from '../../../../app/modules/profile/components/contexts/BlackboxIdContext'
import { useAuth } from '../../../../app/modules/auth'

type Props = {
  className: string
}

const TotalWeightChart: React.FC<Props> = ({ className }) => {
  const chartRef = useRef<HTMLDivElement | null>(null)
  const { mode } = useThemeMode()
  const { currentUser, logout } = useAuth()
  const currentBlackBoxId = useContext(blackboxIdContext);
  const token = currentUser?.token
  const [indicatorGroup, setIndicatorGroup] = useState<any[]>([])
  const [pageInitializion, setPageInitializon] = useState(false)
  const [currentDeviceIndex, setCurrentDeviceIndex] = useState(0)
  const [chartData, setChartData] = useState([])
  const [loading, setLoading] = useState(true)
  // Cihaz listesi cekme denemesi bitti mi (basarili veya bos farketmez).
  const [groupLoaded, setGroupLoaded] = useState(false)

  const userRole = currentUser?.role
  let labels: any = [0, 1, 2]
  let labelsData: any = [0, 1, 2]

  const increaseDeviceIndex = () => {
    if (indicatorGroup.length - 1 > currentDeviceIndex)
      setCurrentDeviceIndex(currentDeviceIndex + 1)
  }
  const decreaseDeviceIndex = () => {
    if (currentDeviceIndex > 0)
      setCurrentDeviceIndex(currentDeviceIndex - 1)
  }
  const refreshMode = () => {
    if (!chartRef.current) {
      return
    }

    const height = parseInt(getCSS(chartRef.current, 'height'))

    const chart = new ApexCharts(chartRef.current, getChartOptions(height, chartData["label1"], chartData["date"]))
    if (chart) {
      chart.render()
    }

    return chart
  }
  const createHeader = () => {
    const headers = new Headers();
    headers.append('Content-Type', 'application/json');
    headers.append('Authorization', "" + token?.toString());
    return headers
  }

  /**
   * Gosterge grubunu ceker.
   *
   * ⚠ Iki hata 2026-10-07'de duzeltildi:
   *
   * 1. `try{...}finally{}` BOSTU ve `catch` yoktu. Istek patlarsa istisna fonksiyondan
   *    disari kaciyor ve en sondaki `setGroupLoaded(true)` HIC CALISMIYORDU -- ekran
   *    "Veri yukleniyor..." ekraninda sonsuza kadar takili kaliyordu. Tam olarak o
   *    bayragin onlemesi gereken durum, hata yolunda yeniden oluyordu.
   *
   * 2. `if (res.status == 401)` -- buradaki `res` AYRISTIRILMIS JSON GOVDESI, Response
   *    nesnesi degil. Govdede `status` alani olmadigi icin kontrol hicbir zaman
   *    tutmuyordu; gercek bir 401'de ise govde JSON bile olmayabilecegi icin
   *    `res.json()` patliyordu. Durum kodu artik Response uzerinden okunuyor.
   *
   * Rol 2 ve 3 disindaki roller icin (superuser dahil) liste bos kaliyor: bu ekranda
   * gosterilecek cihaz yok.
   */
  async function getIndicatorGroup() {
    try {
      let url: string | null = null
      if (userRole === "3") {
        url = BLACKBOXES_GET_INDICATOR_GROUP_BY_COMPANYID + `?companyId=${currentUser?.id}`
      } else if (userRole === "2") {
        url = BLACKBOXES_GET_INDICATOR_GROUP_BY_DISTRIBUTORID + `?distributorId=${currentUser?.id}`
      }
      if (url === null) {
        return
      }
      const response = await fetch(url, {
        method: "GET",
        headers: createHeader(),
      })
      if (response.status === 401) {
        logout()
        return
      }
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`)
      }
      const data = await response.json()
      setIndicatorGroup(Array.isArray(data) ? data : [])
    } catch (error) {
      console.error("Gosterge grubu alinamadi:", error)
      setIndicatorGroup([])
    } finally {
      // "Cekme denemesi bitti" -- basarili da olsa, hata da olsa. Asagidaki efekt bunu
      // bekliyor; calismazsa ekran "Veri yukleniyor..."da takili kalir.
      setGroupLoaded(true)
    }
  }

  /**
   * Secili gostergenin aylik agirlik verisi.
   *
   * 401 kontrolu ayristirilmis govde yerine Response uzerinden yapiliyor (bkz.
   * {@link getIndicatorGroup} javadoc'u, 2. madde). Hata durumunda grafik verisi
   * bosaltiliyor: eskiden `catch` olmadigi icin istisna disari kaciyor, onceki cihazin
   * verisi ekranda kaliyordu -- yani YANLIS cihazin grafigi gosteriliyordu.
   */
  async function getMontlyWeightOneDevice() {
    try {
      const uid = indicatorGroup[currentDeviceIndex]?.["uid"]
      if (!uid) {
        return
      }
      const response = await fetch(BLACKBOXES_BLACKBOX_GRAPHIC_GET_MONTLY_WEIGHT_URL + `?uid=${uid}`, {
        method: "GET",
        headers: createHeader(),
      })
      if (response.status === 401) {
        logout()
        return
      }
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`)
      }
      const res = await response.json()
      setChartData(res)
      labels = (!Array.isArray(res) && (res["date"]?.length != 0 && Array.isArray(res["date"]))) ? res["date"].map(date => date.replace("T", " ")) : [0, 1, 2];
      labelsData = (!Array.isArray(res) && (res["label1"]?.length != 0 && Array.isArray(res["label1"]))) ? res["label1"] : [0, 1, 2];
    } catch (error) {
      console.error("Aylik agirlik verisi alinamadi:", error)
      setChartData([])
    } finally {
      setLoading(false)
    }
  }

  // Cihaz listesi bir kez cekiliyor.
  useEffect(() => {
    if (pageInitializion == false) {
      setPageInitializon(true)
      getIndicatorGroup()
    }
  }, [pageInitializion])

  // Agirlik verisi, cihaz listesi geldiginde ve secili cihaz degistiginde cekiliyor.
  //
  // Onceden bu istek 300 ms'lik bir setTimeout ile atiliyordu. getIndicatorGroup asenkron
  // oldugu icin o anda indicatorGroup genellikle hala bostu ve istek tanimsiz bir uid ile
  // gidip bosa cikiyordu. Liste sonradan geldiginde efekt yeniden calisiyordu ama
  // pageInitializion artik true oldugu icin veri BIR DAHA cekilmiyordu. Grafigin gorunmesi
  // icin ortadaki yaziya elle tiklamak gerekiyordu (o eleman ayni fonksiyonu cagiriyor).
  //
  // Ayrica oklarla cihaz degistirmek yalnizca indeksi degistiriyordu; veri yenilenmedigi
  // icin ekranda onceki cihazin grafigi kaliyordu. Bagimliliga currentDeviceIndex eklendi.
  useEffect(() => {
    if (!groupLoaded) {
      return
    }
    if (indicatorGroup.length > 0) {
      getMontlyWeightOneDevice()
    } else {
      // Liste cekildi ama bos: bu rolde gosterilecek cihaz yok.
      setLoading(false)
    }
  }, [groupLoaded, indicatorGroup, currentDeviceIndex])

  useEffect(() => {
    const chart = refreshMode()


    return () => {
      if (chart) {
        chart.destroy()
      }
    }
  }, [chartRef, mode ,loading,indicatorGroup,chartData])

  if (loading) {
    return <p>Veri yükleniyor...</p>;
  }
  const myStyle = {
   
    padding: '10px',
    cursor: "pointer",
    transition: 'color 0.3s, background-color 0.3s', // Animasyon eklemek için
    
  };
  return (
    <div className={`card ${className}`}>
      {/* begin::Header */}
      <div className='card-header border-0 pt-5 d-flex justify-content-center'>
        <h3 className='card-title align-items-start flex-column'>
          <span className='card-label fw-bold fs-3 mb-1'>Son 6 Ay Taşınan Yük</span>

          {/* <span className='text-muted fw-semibold fs-7'>More than 1000 new records</span> */}
        </h3>

        {/* begin::Toolbar */}
        <div className='card-toolbar' data-kt-buttons='true'>
          <br></br>
          <ul className='pagination'>
            <li className='page-item previous'>
              <a className='page-link' onClick={() => decreaseDeviceIndex()} >
                <i className='previous'></i>
              </a>
            </li>

            <li className='page-item active'>
              <a className='page-link'
              onClick={() => getMontlyWeightOneDevice()}
              style={myStyle}>
                {indicatorGroup.length != 0 && indicatorGroup[currentDeviceIndex]["description"]}
              </a>
            </li>

            <li className='page-item next'>
              <a className='page-link' onClick={() => increaseDeviceIndex()} >
                <i className='next'></i>
              </a>
            </li>
          </ul>
        </div>
        {/* end::Toolbar */}
      </div>
      {/* end::Header */}

      {/* begin::Body */}
      <div className='card-body'>
        {/* begin::Chart */}
        <div ref={chartRef} id='kt_charts_widget_3_chart' style={{ height: '350px' }}></div>
        {/* end::Chart */}
      </div>
      {/* end::Body */}
    </div>
  )
}

export { TotalWeightChart }

function getChartOptions(height: number, label1, label2): ApexOptions {
  const labelColor = getCSSVariableValue('--bs-gray-500')
  const borderColor = getCSSVariableValue('--bs-gray-200')
  const baseColor = getCSSVariableValue('--bs-info')
  const lightColor = getCSSVariableValue('--bs-info-light')
  
  return {
    series: [
      {
        name: 'Toplam Yük',
        data: label1,
      },
    ],
    chart: {
      fontFamily: 'inherit',
      type: 'area',
      height: 350,
      toolbar: {
        show: false,
      },
    },
    plotOptions: {},
    legend: {
      show: false,
    },
    dataLabels: {
      enabled: false,
    },
    fill: {
      type: 'solid',
      opacity: 1,
    },
    stroke: {
      curve: 'smooth',
      show: true,
      width: 3,
      colors: [baseColor],
    },
    xaxis: {
      categories: label2,
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
      labels: {
        style: {
          colors: labelColor,
          fontSize: '12px',
        },
      },
      crosshairs: {
        position: 'front',
        stroke: {
          color: baseColor,
          width: 1,
          dashArray: 3,
        },
      },
      tooltip: {
        enabled: true,
        formatter: undefined,
        offsetY: 0,
        style: {
          fontSize: '12px',
        },
      },
    },
    yaxis: {
      labels: {
        style: {
          colors: labelColor,
          fontSize: '12px',
        },
        // Eksende birim yazmiyoruz, yalnizca kisaltma: 261058 -> "261 t"
        formatter: function (val) {
          return formatWeightShort(val)
        },
      },
    },
    states: {
      normal: {
        filter: {
          type: 'none',
          value: 0,
        },
      },
      hover: {
        filter: {
          type: 'none',
          value: 0,
        },
      },
      active: {
        allowMultipleDataPointsSelection: false,
        filter: {
          type: 'none',
          value: 0,
        },
      },
    },
    tooltip: {
      style: {
        fontSize: '12px',
      },
      y: {
        formatter: function (val) {
          return formatWeight(val)
        },
      },
    },
    colors: [lightColor],
    grid: {
      borderColor: borderColor,
      strokeDashArray: 4,
      yaxis: {
        lines: {
          show: true,
        },
      },
    },
    markers: {
      strokeColors: baseColor,
      strokeWidth: 3,
    },
  }
}
